import { useEffect, useMemo, useState } from 'react';
import { PageCanvas } from './PageCanvas.jsx';
import { AnnotationLayer } from './AnnotationLayer.jsx';
import { TextLayer } from './TextLayer.jsx';
import { usePdf } from '../store/pdfStore.js';
import { useConfig } from '../store/configStore.js';
import { useT } from '../i18n/index.js';
import { Icon } from './Icon.jsx';

export function PageEditor({ doc }) {
  const t = useT();
  const pages = usePdf((s) => s.pages);
  const currentPageId = usePdf((s) => s.currentPageId);
  const openPage = usePdf((s) => s.openPage);
  const goAll = usePdf((s) => s.goAll);
  const rotatePage = usePdf((s) => s.rotatePage);
  const duplicatePage = usePdf((s) => s.duplicatePage);
  const deletePage = usePdf((s) => s.deletePage);
  const editor = useConfig((s) => s.config.settings.editor);
  const renderScale = useConfig((s) => s.config.settings.renderScale);

  const [size, setSize] = useState({ width: 0, height: 0 });

  const idx = useMemo(() => pages.findIndex((p) => p.id === currentPageId), [pages, currentPageId]);
  const page = pages[idx];

  useEffect(() => {
    if (!page && pages.length) openPage(pages[0].id);
  }, [page, pages, openPage]);

  if (!page) return null;

  const goPrev = () => idx > 0 && openPage(pages[idx - 1].id);
  const goNext = () => idx < pages.length - 1 && openPage(pages[idx + 1].id);

  return (
    <div className="page-editor">
      <div className="editor-toolbar">
        <button className="btn sm" onClick={goAll}>
          <Icon name="rotateCCW" size={14} /> {t('toolbar.back_to_grid')}
        </button>
        <span className="divider" />
        <button className="btn sm" onClick={() => rotatePage(page.id, -90)} title={t('toolbar.rotate_left')}>
          <Icon name="rotateCCW" size={14} />
        </button>
        <button className="btn sm" onClick={() => rotatePage(page.id, 90)} title={t('toolbar.rotate_right')}>
          <Icon name="rotateCW" size={14} />
        </button>
        <button className="btn sm" onClick={() => duplicatePage(page.id)} title={t('toolbar.duplicate')}>
          <Icon name="copy" size={14} />
        </button>
        <button className="btn sm danger" onClick={() => { deletePage(page.id); goAll(); }} title={t('toolbar.delete')}>
          <Icon name="trash" size={14} />
        </button>

        <span style={{ marginLeft: 'auto' }} />
        <button className="btn icon sm" onClick={goPrev} disabled={idx === 0} aria-label={t('editor.prev')} title={`${t('editor.prev')} · ←`}>
          <Icon name="chevLeft" size={14} />
        </button>
        <span className="chip">{t('editor.page_indicator', { i: idx + 1, n: pages.length })}</span>
        <button className="btn icon sm" onClick={goNext} disabled={idx === pages.length - 1} aria-label={t('editor.next')} title={`${t('editor.next')} · →`}>
          <Icon name="chevRight" size={14} />
        </button>
      </div>

      <div className="page-stage">
        <div className="page-wrap" style={{ width: size.width || undefined, height: size.height || undefined }}>
          <PageCanvas
            doc={doc}
            originalIndex={page.originalIndex}
            rotation={page.rotation}
            scale={renderScale}
            onSize={setSize}
          />
          {page.rotation === 0 && size.width > 0 && (
            <TextLayer doc={doc} page={page} scale={renderScale} />
          )}
          {size.width > 0 && (
            <AnnotationLayer
              page={page}
              size={size}
              scale={renderScale}
              defaults={{
                font: editor.defaultFont,
                size: editor.defaultFontSize,
                color: editor.defaultColor,
                bold: editor.defaultBold,
                italic: editor.defaultItalic,
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
